/**
 * Session documents — the file side of a session's Documents tab.
 *
 * Pure helpers (what kind of document a file is, where it lives, how big it
 * is) plus the three storage calls the panel and the store need: upload, open
 * through a signed link, remove. Everything lands in the private bucket
 * `session-documents` (migration 818) at
 * {workspace_id}/{session_id}/{artifact_id}/{filename}, and the matching
 * `session_artifacts` row carries `storage_path`, `mime_type`, `size_bytes`.
 *
 * Canon: Doc 21 §6.4 — "accept a recording URL or file upload as a single
 * session-level artifact". Brief K §2a. Roadmap D-5.
 */

import { getSafeSupabaseClient } from './supabaseSync';
import type { SessionArtifactType } from '../types/prioritization/sessionArtifacts';

export const SESSION_DOCUMENTS_BUCKET = 'session-documents';

/** Matches the bucket's file_size_limit in migration 818. */
export const SESSION_DOCUMENT_MAX_BYTES = 524_288_000; // 500 MB

/** What the file picker offers. Mirrors the bucket's allowed_mime_types. */
export const SESSION_DOCUMENT_ACCEPT =
  'audio/*,video/*,.pdf,.txt,.vtt,.md,.doc,.docx';

/** Browsers leave `file.type` empty for some extensions; fill the common ones. */
const MIME_BY_EXTENSION: Record<string, string> = {
  vtt: 'text/vtt',
  md: 'text/markdown',
  txt: 'text/plain',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  webm: 'video/webm',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
};

export type SessionDocumentFileType = Extract<
  SessionArtifactType,
  'audio_file' | 'video_file' | 'document_file'
>;

/** The media type to store and send: the browser's, or one read from the extension. */
export function mimeTypeForFile(file: { name: string; type?: string }): string {
  const fromBrowser = (file.type ?? '').trim();
  if (fromBrowser) return fromBrowser;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXTENSION[ext] ?? 'application/octet-stream';
}

/** Which of the three file kinds a file is, from its media type. */
export function documentTypeForFile(file: { name: string; type?: string }): SessionDocumentFileType {
  const mime = mimeTypeForFile(file);
  if (mime.startsWith('audio/')) return 'audio_file';
  if (mime.startsWith('video/')) return 'video_file';
  return 'document_file';
}

/**
 * Where the object lives. One folder per artifact so two uploads with the
 * same filename never collide; the workspace id first so the storage policies
 * (818) can read it with split_part(name, '/', 1).
 */
export function sessionDocumentPath(params: {
  workspaceId: string;
  sessionId: string;
  artifactId: string;
  fileName: string;
}): string {
  const safeName = params.fileName
    .replace(/[^\w.\-() ]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180) || 'file';
  return `${params.workspaceId}/${params.sessionId}/${params.artifactId}/${safeName}`;
}

/** "42 MB", "1.2 GB", "900 KB" — for the row, not for arithmetic. */
export function describeFileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** Why a file cannot be added, in plain words — or null when it can. */
export function sessionDocumentProblem(file: { name: string; size: number; type?: string }): string | null {
  if (file.size <= 0) return 'That file is empty.';
  if (file.size > SESSION_DOCUMENT_MAX_BYTES) {
    return `That file is ${describeFileSize(file.size)}. The limit is ${describeFileSize(SESSION_DOCUMENT_MAX_BYTES)}.`;
  }
  return null;
}

export interface UploadedSessionDocument {
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  artifactType: SessionDocumentFileType;
}

/** Put the file in the bucket. Throws with a plain-words message on failure. */
export async function uploadSessionDocument(params: {
  workspaceId: string;
  sessionId: string;
  artifactId: string;
  file: File;
}): Promise<UploadedSessionDocument> {
  const problem = sessionDocumentProblem(params.file);
  if (problem) throw new Error(problem);

  const client = getSafeSupabaseClient();
  if (!client) throw new Error('Not connected to the database, so the file cannot be saved.');

  const storagePath = sessionDocumentPath({
    workspaceId: params.workspaceId,
    sessionId: params.sessionId,
    artifactId: params.artifactId,
    fileName: params.file.name,
  });
  const mimeType = mimeTypeForFile(params.file);

  const { error } = await client.storage
    .from(SESSION_DOCUMENTS_BUCKET)
    .upload(storagePath, params.file, { contentType: mimeType, upsert: false });
  if (error) throw new Error(`The file could not be saved: ${error.message}`);

  return {
    storagePath,
    mimeType,
    sizeBytes: params.file.size,
    artifactType: documentTypeForFile(params.file),
  };
}

/** A link that opens the file for the next hour. Only a workspace member can mint one (818 policies). */
export async function getSessionDocumentUrl(storagePath: string): Promise<string> {
  const client = getSafeSupabaseClient();
  if (!client) throw new Error('Not connected to the database, so the file cannot be opened.');
  const { data, error } = await client.storage
    .from(SESSION_DOCUMENTS_BUCKET)
    .createSignedUrl(storagePath, 60 * 60);
  if (error || !data?.signedUrl) {
    throw new Error(`The file could not be opened: ${error?.message ?? 'no link came back'}`);
  }
  return data.signedUrl;
}

/** Remove the object. Best effort: a missing object is not an error worth surfacing. */
export async function removeSessionDocument(storagePath: string): Promise<void> {
  const client = getSafeSupabaseClient();
  if (!client) return;
  const { error } = await client.storage.from(SESSION_DOCUMENTS_BUCKET).remove([storagePath]);
  if (error) console.warn('[sessionDocuments] could not remove file:', error.message);
}
